import type { ActionButtonData } from '../action-button/ActionButton';
import type { LabeledCheckboxData } from '../labeled-checkbox/LabeledCheckbox';
import type { UndoActionButtonData } from '../undo-action-button/UndoActionButton';
import { t } from '../../services/locale';
import type { ProfileHostPorts } from '../../services/profileHost';
import { resolveDocuments } from './profileDocumentRules';
import { userCanChangeImage } from './profileIdentityRules';
import type { ProfileDialogData } from './ProfileDialog';
import type { ProfileBooking } from './profileBooking.types';
import type {
  ProfileBranchDocument,
  ProfileDocumentRow,
  ProfileIdentity,
  ProfileTransaction,
  ProfileUserDocument,
} from './profileAccount.types';
import type { ProfileLinkedAccount } from './ProfileLinkedAccounts';
import type { ProfilePaymentMethodKind } from './ProfilePaymentMethods';
import {
  branchAccepts,
  hasSubscriptionMembership,
  memberHasCard,
  memberHasMandate,
  savedCardLabel,
  savedMandateLabel,
  type ConsentChannel,
  type ConsentPurpose,
  type ProfileRuntimeBranch,
  type ProfileRuntimeUser,
  type RuntimeMembership,
} from './profileRuntime';
import type { ProfileMembershipListData } from './ProfileMembershipList';

export type ProfileViewState = {
  user: ProfileRuntimeUser | null;
  branch: ProfileRuntimeBranch | null;
  profile: ProfileRuntimeUser | null;
  tab: string;
  editProfile: boolean;
  isAddressExpanded: boolean;
  bookings: ProfileBooking[];
  bookingsLoaded: boolean;
  showContactAdminMessage: boolean;
  isInteractiveSpotBookingEnabled: boolean;
  creditsAvailable: number | null;
  credits: Array<Record<string, unknown>>;
  stackedMemberships: RuntimeMembership[];
  transactions: ProfileTransaction[];
  totalTransactions: number;
  moreTransactions: boolean;
  linkedAccounts: ProfileLinkedAccount[];
  isChildAccount: boolean;
  isGuardAccount: boolean;
  userDocuments: ProfileUserDocument[] | null;
  branchDocuments: ProfileBranchDocument[] | null;
  userImagePreviewUrl: string | null;
  imageVersion: number;
  hasUploadedProfileImage: boolean;
  saveErrors: string[];
  showSaveErrors: boolean;
  isInputDisabled: boolean;
  userConsents: ConsentChannel[];
  hasSmsWaiver: boolean;
  mailToContactStudio: string;
  cancellationAlertText: string;
  autoBookingIsEnabled: boolean;
  isClickToCancelEnabled: boolean;
  updateProfileRestricted: boolean;
  mandatoryEmergencyContact: boolean;
  embedInHostModal?: boolean;
};

export type ProfileViewActions = {
  changeTab: (tab: string) => void;
  startEdit: () => void;
  toggleAddress: () => void;
  logOut: () => void;
  openFamilyAccounts: () => void;
  patchProfile: (patch: Partial<ProfileIdentity>) => void;
  patchAddress: (patch: Partial<NonNullable<ProfileIdentity['address']>>) => void;
  selectImage: (file: File | null) => void;
  save: () => void;
  changePassword: () => void;
  showRestricted: () => void;
  showFirstImage: () => void;
  setConsent: (channelKey: string, purposeKey: string, next: boolean) => void;
  openSmsWaiver: () => void;
  cancelBooking: (booking: ProfileBooking) => void;
  showMoreTransactions: () => void;
  pauseMembership: (membership: RuntimeMembership) => void;
  cancelMembership: (membership: RuntimeMembership) => void;
  manageMembership: (membership: RuntimeMembership) => void;
  buyMembership: () => void;
  goPayg: () => void;
  trackContactStudio: () => void;
  useAccount: (account: ProfileLinkedAccount) => void;
  addLinkedAccount: () => void;
  switchToParent: () => void;
  previewWaiver: () => void;
  openDocument: (document: ProfileDocumentRow) => void;
  addOrReplacePayment: (method: ProfilePaymentMethodKind) => void;
  deletePayment: (method: ProfilePaymentMethodKind) => void;
};

function action(labelKey: string, extra: ActionButtonData = {}): ActionButtonData {
  return { label: t(labelKey), variant: 'primary', ...extra };
}

function rowAction(labelKey: string, extra: ActionButtonData): ActionButtonData {
  return { ...extra, label: t(labelKey) };
}

function closeLabel(): string {
  const translated = t('WEB_CLOSE');
  return translated === 'WEB_CLOSE' ? 'Close' : translated;
}

function consentBoxes(state: ProfileViewState, actions: ProfileViewActions, host: ProfileHostPorts): LabeledCheckboxData[] {
  const params = host.consentTranslationParams();
  const boxes: LabeledCheckboxData[] = [];
  state.userConsents.forEach((channel) => {
    Object.keys(channel).forEach((purposeKey) => {
      if (purposeKey === 'key') {
        return;
      }
      const purpose = channel[purposeKey];
      if (!purpose || typeof purpose !== 'object') {
        return;
      }
      const purposeData = purpose as ConsentPurpose;
      const showLink = channel.key === 'sms' && state.hasSmsWaiver;
      boxes.push({
        checkboxId: `profile-consent-${channel.key}-${purposeKey}`,
        translationParams: params,
        dataAttributes: {
          'data-consent-channel': channel.key,
          'data-consent-purpose': purposeKey,
        },
        accepted: !!purposeData.status,
        agreeLabelKey: purposeData.member_text,
        linkLabelKey: showLink ? 'WEB_GDPR_CONSENT_SMS_LINK_TEXT' : undefined,
        onClickLink: showLink ? actions.openSmsWaiver : undefined,
        onChange: (next) => actions.setConsent(channel.key, purposeKey, next),
      });
    });
  });
  return boxes;
}

function paygAction(actions: ProfileViewActions): UndoActionButtonData {
  return {
    label: t('WEB_BACK_TO_PAYG'),
    undoLabel: t('WEB_UNDO'),
    id: 'back-to-payg-button',
    dataTestId: 'profile-back-to-payg-button',
    variant: 'primary',
    durationMs: 3000,
    onCommit: actions.goPayg,
  };
}

function familyTabVisible(state: ProfileViewState): boolean {
  if (state.isGuardAccount) {
    return true;
  }
  return state.isChildAccount;
}

function projectTabs(state: ProfileViewState, actions: ProfileViewActions): ProfileDialogData['tabs'] {
  const items = [
    { value: 'my-bookings', label: t('WEB_MY_BOOKINGS'), id: 'profile-my-bookings-tab' },
    { value: 'my-membership', label: t('WEB_MY_MEMBERSHIP'), id: 'profile-my-membership-tab' },
    { value: 'my-transactions', label: t('WEB_MY_TRANSACTIONS'), id: 'profile-my-transactions-tab' },
  ];
  if (familyTabVisible(state)) {
    items.push({ value: 'linked-accounts', label: t('WEB_FAMILY_ACCOUNTS'), id: 'profile-family-accounts-tab' });
  }
  return {
    className: 'horizontal-scroll-tabs--profile',
    activeValue: state.tab,
    onTabClick: actions.changeTab,
    items,
  };
}

function projectSummary(state: ProfileViewState, actions: ProfileViewActions) {
  return {
    profile: state.profile,
    branch: state.branch,
    creditsAvailable: state.creditsAvailable,
    imageVersion: state.imageVersion,
    isAddressExpanded: state.isAddressExpanded,
    isGuardAccount: state.isGuardAccount,
    linkedAccountCount: state.linkedAccounts.length,
    onLogOut: actions.logOut,
    onEdit: actions.startEdit,
    onToggleAddress: actions.toggleAddress,
    onOpenFamilyAccounts: actions.openFamilyAccounts,
  };
}

function projectEditForm(
  state: ProfileViewState,
  actions: ProfileViewActions,
  host: ProfileHostPorts
) {
  return {
    profile: state.profile,
    branch: state.branch,
    imageVersion: state.imageVersion,
    imagePreviewUrl: state.userImagePreviewUrl,
    hasUploadedProfileImage: state.hasUploadedProfileImage,
    updateProfileRestricted: state.updateProfileRestricted,
    isInputDisabled: state.isInputDisabled,
    isAddressExpanded: state.isAddressExpanded,
    mandatoryEmergencyContact: state.mandatoryEmergencyContact,
    canChangeImage: userCanChangeImage({
      hasUser: !!state.user?._id,
      hasBranch: !!state.branch?._id,
      updateProfileRestricted: state.updateProfileRestricted,
      hasUploadedProfileImage: state.hasUploadedProfileImage,
    }),
    errors: state.showSaveErrors ? state.saveErrors : [],
    consentCheckboxes: consentBoxes(state, actions, host),
    saveActionData: action('WEB_SAVE_CHANGES', {
      dataTestId: 'profile-save-changes-button',
      type: 'submit',
    }),
    onLogOut: actions.logOut,
    onToggleAddress: actions.toggleAddress,
    onShowRestrictedDialog: actions.showRestricted,
    onShowFirstTimeImageDialog: actions.showFirstImage,
    onProfilePatch: actions.patchProfile,
    onAddressPatch: actions.patchAddress,
    onSelectImage: actions.selectImage,
    onSave: actions.save,
  };
}

function documentUrl(
  host: ProfileHostPorts,
  state: ProfileViewState,
  documentId: string,
  userMembershipId: string | false
): string {
  const branchId = state.branch?._id ?? '';
  const userId = state.user?._id ?? '';
  return host.getDocumentUrl(branchId, userId, documentId, userMembershipId);
}

function projectDocuments(
  state: ProfileViewState,
  actions: ProfileViewActions,
  host: ProfileHostPorts
) {
  return {
    documents: resolveDocuments(state.userDocuments, state.branchDocuments, {
      branch: state.branch,
      user: state.user,
      formatLocalDate: host.formatLocalDate,
      resolveDocumentUrl: (documentId, userMembershipId) => documentUrl(host, state, documentId, userMembershipId),
    }),
    onPreviewStudioWaiver: actions.previewWaiver,
    onOpenDocument: actions.openDocument,
  };
}

function projectPaymentMethods(state: ProfileViewState, actions: ProfileViewActions) {
  const profile = state.profile;
  return {
    branchAcceptsDirectDebit: branchAccepts(state.branch, 'direct_debit'),
    branchAcceptsCard: branchAccepts(state.branch, 'card'),
    isChildAccount: state.isChildAccount,
    hasMandate: memberHasMandate(profile),
    hasCard: memberHasCard(profile),
    hasSubscriptionMembership: hasSubscriptionMembership(state.user),
    mandateLabel: savedMandateLabel(profile),
    cardLabel: savedCardLabel(profile),
    addMandateActionData: action('WEB_ADD', { id: 'add-mandate-button', dataTestId: 'profile-add-mandate-button' }),
    deleteMandateActionData: action('WEB_DELETE', { id: 'delete-mandate-button', dataTestId: 'profile-delete-mandate-button' }),
    replaceMandateActionData: action('WEB_REPLACE', { id: 'replace-mandate-button', dataTestId: 'profile-replace-mandate-button' }),
    addCardActionData: action('WEB_ADD', { id: 'add-card-button', dataTestId: 'profile-add-card-button' }),
    deleteCardActionData: action('WEB_DELETE', { id: 'delete-card-button', dataTestId: 'profile-delete-card-button' }),
    replaceCardActionData: action('WEB_REPLACE', { id: 'replace-card-button', dataTestId: 'profile-replace-card-button' }),
    onAddOrReplacePayment: actions.addOrReplacePayment,
    onDeletePayment: actions.deletePayment,
  };
}

function projectBookings(
  state: ProfileViewState,
  actions: ProfileViewActions,
  host: ProfileHostPorts
) {
  return {
    bookings: state.bookings,
    bookingsLoaded: state.bookingsLoaded,
    nowUnix: Math.floor(Date.now() / 1000),
    branch: state.branch,
    formatUnix: host.formatUnix,
    ordinal: host.ordinal,
    autoBookingIsEnabled: state.autoBookingIsEnabled,
    showContactAdminMessage: state.showContactAdminMessage,
    isInteractiveSpotBookingEnabled: state.isInteractiveSpotBookingEnabled,
    onCancelBooking: actions.cancelBooking,
    getCancelActionData: (booking: ProfileBooking) => rowAction('WEB_CANCEL', {
      id: `profile-booking-${booking._id ?? ''}-cancel`,
      dataTestId: `profile-booking-${booking._id ?? ''}-cancel`,
    }),
    getScheduleCloseActionData: () => rowAction('WEB_CLOSE', { type: 'button' }),
  };
}

function projectMembership(
  state: ProfileViewState,
  actions: ProfileViewActions,
  host: ProfileHostPorts
) {
  return {
    stackedMemberships: state.stackedMemberships,
    user: state.user,
    branch: state.branch,
    formatters: {
      formatUnix: host.formatUnix,
      formatLocalDate: host.formatLocalDate,
    },
    todayLocal: host.todayLocal(),
    isClickToCancelEnabled: state.isClickToCancelEnabled,
    requestedMembershipCancellationAlertText: state.cancellationAlertText,
    creditsAvailable: state.creditsAvailable,
    credits: state.credits as ProfileMembershipListData['credits'],
    mailToContactStudio: state.mailToContactStudio,
    getPauseActionData: () => rowAction('WEB_PAUSE_MEMBERSHIP', { dataTestId: 'membership-pause-button' }),
    getCancelActionData: () => rowAction('WEB_CANCEL_YOUR_MEMBERSHIP', { dataTestId: 'membership-cancel-button' }),
    getManageActionData: () => rowAction('WEB_MANAGE_MEMBERSHIP', {
      dataTestId: 'membership-manage-button',
      variant: 'secondary',
      leadingIcon: 'manage-membership',
      trailingIcon: 'icon-arrow-right',
    }),
    buyMembershipActionData: action('WEB_BUY_MEMBERSHIP', {
      id: 'buy-membership-button',
      dataTestId: 'profile-buy-membership-button',
    }),
    backToPaygActionData: paygAction(actions),
    onPause: actions.pauseMembership,
    onCancelMembership: actions.cancelMembership,
    onManage: actions.manageMembership,
    onBuyMembership: actions.buyMembership,
    onTrackContactStudio: actions.trackContactStudio,
  };
}

function projectTransactions(
  state: ProfileViewState,
  actions: ProfileViewActions,
  host: ProfileHostPorts
) {
  return {
    transactions: state.transactions,
    totalTransactions: state.totalTransactions,
    moreTransactions: state.moreTransactions,
    branch: state.branch,
    formatUnix: host.formatUnix,
    formatMoney: host.formatMoney,
    onShowMore: actions.showMoreTransactions,
  };
}

function linkedAccountAddLabel(count: number): string {
  if (count === 0) {
    return 'WEB_ADD_ACCOUNT';
  }
  return 'WEB_ADD_ANOTHER_ACCOUNT';
}

function projectLinkedAccounts(state: ProfileViewState, actions: ProfileViewActions) {
  return {
    isGuardAccount: state.isGuardAccount,
    isChildAccount: state.isChildAccount,
    linkedAccounts: state.linkedAccounts,
    addLinkedAccountActionData: action(
      linkedAccountAddLabel(state.linkedAccounts.length),
      { id: 'add-child-account-button', dataTestId: 'profile-add-linked-account-button' }
    ),
    switchToParentAccountActionData: action('WEB_SWITCH_BACK_TO_YOUR_ACCOUNT', {
      dataTestId: 'profile-switch-to-parent-account-button',
    }),
    getUseAccountActionData: () => rowAction('WEB_USE_ACCOUNT', { dataTestId: 'linked-account-use-button' }),
    onUseAccount: actions.useAccount,
    onAddLinkedAccount: actions.addLinkedAccount,
    onSwitchToParent: actions.switchToParent,
  };
}

function closeAction(): ActionButtonData {
  return {
    id: 'close-profile-button',
    ariaLabel: closeLabel(),
    variant: 'primary',
    type: 'button',
    leadingIcon: 'icon-x',
    iconOnly: true,
    dismissModal: true,
  };
}

export function projectProfileDialogData(
  state: ProfileViewState,
  actions: ProfileViewActions,
  host: ProfileHostPorts
): ProfileDialogData {
  const profile = state.profile;
  return {
    profileDialogEnabled: true,
    profilePageEnabled: true,
    embedInHostModal: state.embedInHostModal,
    editProfile: state.editProfile,
    firstName: profile?.first_name,
    lastName: profile?.last_name,
    activeTab: state.tab,
    closeActionData: closeAction(),
    tabs: projectTabs(state, actions),
    summary: projectSummary(state, actions),
    editForm: projectEditForm(state, actions, host),
    documents: projectDocuments(state, actions, host),
    paymentMethods: projectPaymentMethods(state, actions),
    changePasswordActionData: action('WEB_CHANGE_PASSWORD', {
      id: 'change-password-button',
      dataTestId: 'profile-change-password-button',
    }),
    onChangePassword: actions.changePassword,
    bookings: projectBookings(state, actions, host),
    membership: projectMembership(state, actions, host),
    transactions: projectTransactions(state, actions, host),
    linkedAccounts: projectLinkedAccounts(state, actions),
  };
}
